import api from '@/api_alv'
import i18n from '@/assets/i18n/i18n'

const DIR = "erp/inventories"

const state = {
// List
    inventories: [],
    selectedInventories: [],
    expandedInventories: [],

// Filters
    inventoriesSearch: '',
    inventoriesWarehousesOptions: [],
    inventoriesValueTypes: 'allValues',
    inventoriesDetailsStockToDate: null,
    selectedInventoriesWarehouses: null,
    selectedInventoriesValueTypes: null,

// Import
    showInventoriesImportDialog: false,
    selectedInventoriesImport: [],

// Details
    inventoriesDetails: {},

// Export enabled only after a search was triggered; disabled again on any filter change
    inventoriesExportEnabled: false,
}

const getters = {
// List
    getInventories(state)                   { return state.inventories },
    getSelectedInventories(state)           { return state.selectedInventories },
    getExpandedInventories(state)           { return state.expandedInventories },

// Filters
    getInventoriesSearch(state)             { return state.inventoriesSearch },
    getInventoriesWarehousesOptions(state)  { return state.inventoriesWarehousesOptions },
    getSelectedInventoriesWarehouses(state) { return state.selectedInventoriesWarehouses },
    getInventoriesValueTypes(state)         { return state.inventoriesValueTypes },
    getSelectedInventoriesValueTypes(state) { return state.selectedInventoriesValueTypes },
    getInventoriesDetailsStockToDate(state) { return state.inventoriesDetailsStockToDate },

// Import
    getShowInventoriesImportDialog(state)   { return state.showInventoriesImportDialog },
    getSelectedInventoriesImport(state)     { return state.selectedInventoriesImport },

// Details
    getInventoriesDetails(state)            { return state.inventoriesDetails },

// Export
    getInventoriesExportEnabled(state)      { return state.inventoriesExportEnabled },
}

const mutations = {
// List
    setInventories:                         (state, inventories) => { state.inventories = inventories },
    setSelectedInventories:                 (state, selected) => { state.selectedInventories = selected },
    setExpandedInventories:                 (state, inventories) =>  { 
        if(Object.keys(inventories).length === 0) state.expandedInventories = []
        else state.expandedInventories.push(inventories) 
    },

// Filters (any change disables Export until the next search)
    setInventoriesSearch:                   (state, item) => { state.inventoriesSearch = item; state.inventoriesExportEnabled = false },
    setInventoriesWarehousesOptions:        (state, item) => { state.inventoriesWarehousesOptions = item },
    setSelectedInventoriesWarehouses:       (state, item) => { state.selectedInventoriesWarehouses = item; state.inventoriesExportEnabled = false },
    setInventoriesValueTypes:               (state, item) => { state.inventoriesValueTypes = item },
    setSelectedInventoriesValueTypes:       (state, item) => { state.selectedInventoriesValueTypes = item; state.inventoriesExportEnabled = false },
    setInventoriesDetailsStockToDate:       (state, item) => { state.inventoriesDetailsStockToDate = item; state.inventoriesExportEnabled = false },

// Import
    setShowInventoriesImportDialog:         (state, show) => { state.showInventoriesImportDialog = show },
    setSelectedInventoriesImport:           (state, item) => { state.selectedInventoriesImport = item },

// Details
    setInventoriesDetails:                  (state, details) => { state.inventoriesDetails = details },

// Export
    setInventoriesExportEnabled:            (state, enabled) => { state.inventoriesExportEnabled = enabled },
}

const actions = {
    // LIST
    async fetchInventories({ commit, getters }) {
        commit('setIsLoading', true)

        try {
            // check for set filters
            let filterObj = {
                'search' :  getters.getInventoriesSearch,
                'warehouseId' : getters.getSelectedInventoriesWarehouses,
                'valueTypes' : getters.getSelectedInventoriesValueTypes,
                'stockToDate' : getters.getInventoriesDetailsStockToDate,
            }

            // create string of filter params for api call
            let paramsString = this._vm.$alvPlugin.createApiUrlFilterString(filterObj)

            // Get list items from db
            let response = await api.getListData(DIR, getters.getCurrentPage, paramsString)

            if(!response || response.error === 1) {
                commit('setInventories', [])
                let errorMsg = response && response.response && response.response.errorMsg
                commit('setSnackbar', {
                    type: 'error',
                    msg: errorMsg !== undefined
                        ? (this._vm.$alvPlugin.isLangConst(errorMsg) ? i18n.t(errorMsg) : errorMsg)
                        : 'Daten konnten nicht oder nur unvollständig geladen werden'
                })
                return
            }

            let list = response.list || []
            if(list.length === 0) {
                commit('setNoDataText', 'lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')
                commit('setInventories', [])
            } else {
                commit('setInventories', list)
            }

            this._vm.$alvPlugin.storePagingInformations(response.filteredRecords, response.currentPage, response.totalRecords)
            commit('setInventoriesExportEnabled', true)
        } finally {
            commit('setIsLoading', false)
        }
    },
    
    // DETAILS
    async fetchInventoriesDetails({ commit }, alvId) {
        let values

        let response = await api.getDetailsById(DIR, alvId)
        
        if(response === undefined || response.length === 0 || response.error === 1) {
            response = {}
            commit('setSnackbar', {
                type: 'error',
                msg: 'Daten konnten nicht oder nur unvollständig geladen werden'
            })
        } else values = response

        commit('setInventoriesDetails', values)
        commit('setIsLoading', false)
    },

    // INVENTORIES WAREHOUSES (dropdown)
    async fetchDropdownInventoriesWarehouses({ commit }) {
        let response = await api.getAzByType(['lager']) // TODO: shall also include Own Warehouse, also translate to EN
        commit('setInventoriesWarehousesOptions', response.list)
    },

    async clearFilters({ commit }) {
        commit('setInventoriesSearch', '')
        commit('setSelectedInventoriesWarehouses', null)
        commit('setSelectedInventoriesValueTypes', null)
        commit('setInventoriesDetailsStockToDate', this._vm.$alvPlugin.getTodaysDateYYYYMMDD())
    },

    // Import
    async doInventoriesImport({ getters, commit }) {
        let tempText = i18n.t('lang_ERP_INVENTORIES_IMPORT_DIALOG_TEXT_UPLOAD_FILE') + ' ' + i18n.t('lang_GENERAL_CAPTION_SELECT_FILE_OF_TYPE')
        let allImporters = getters.getInventoriesWarehousesOptions
        let selectedImporter = getters.getSelectedInventoriesImport
        let dialogText = ''

        let match = allImporters.find(importer => {
            return importer.alvId === selectedImporter
        })

        dialogText = tempText.replace(/<warehouseName>|<join>/gi, matched => {
            return {
                '<warehouseName>': match.gemaName,
                '<join>': i18n.t('lang_GENERAL_CAPTION_XLSX')
            }[matched]
        })

        commit('setImportDialogText', dialogText)   
        commit('setShowInventoriesImportDialog', true)
    },

        // Action: EXPORT ALL
        async exportAllInventories({ commit, getters }) {
            commit('setIsLoading', true)
            let queryParams = {
                "action" : "export",
                "type" : "excel",
            }
    
            let metaData = { "releaseIds": [] }
    
            let response = await api.triggerAction(DIR, metaData, queryParams)
            if(response.error === -1) {
                if(response.response.path !== null) {
                    api.getFileFromALV(response.response.path)
                    commit('setSnackbar', {
                        type: 'success',
                        msg: i18n.t('lang_GENERAL_SNACKBAR_DOWNLOAD_SUCCESSFULL')
                    })
                }
            } else {
                let errorMsg = response.response.errorMsg
                if(errorMsg !== undefined) {
                    commit('setSnackbar', {
                        type: 'error',
                        msg: this._vm.$alvPlugin.isLangConst(errorMsg) ? i18n.t(errorMsg) : errorMsg
                    })
                }
            }
            commit('setIsLoading', false)
        },

    //Minimum Stock
    async createMinStockReport({  commit }){
        let queryParams = {'action' : 'minimumStock'}
        let metaData = {}

        let response = await api.triggerAction(DIR,metaData,queryParams)

        if(response === undefined || response.length === 0 || response.error === 1) {
            commit('setSnackbar', {
                type: 'error',
                msg: i18n.t('lang_GENERIC_SNACKBAR_DOWNLOAD_NOT_SUCCESSFULL')
            })
        }else{
            api.getFileFromALV(response.response.path)
            commit('setSnackbar', {
                type: 'success',
                msg: i18n.t('lang_GENERAL_SNACKBAR_DOWNLOAD_SUCCESSFULL')
            })
        }
    }
}

export default {
    state, getters, actions, mutations
}